import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import './ToastProvider.css';

type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastDescriptor {
  id: string;
  message: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ToastContextValue {
  pushToast: (toast: Omit<ToastDescriptor, 'id'>) => string;
  dismissToast: (toastId: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function randomId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastDescriptor[]>([]);
  const timersRef = useRef<Record<string, number>>({});

  const dismissToast = useCallback((toastId: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== toastId));
    const timer = timersRef.current[toastId];
    if (timer) {
      window.clearTimeout(timer);
      delete timersRef.current[toastId];
    }
  }, []);

  const scheduleRemoval = useCallback(
    (toastId: string, durationMs: number) => {
      if (durationMs <= 0) {
        return;
      }
      const timer = window.setTimeout(() => {
        dismissToast(toastId);
      }, durationMs);
      timersRef.current[toastId] = timer;
    },
    [dismissToast]
  );

  const pushToast = useCallback(
    (toast: Omit<ToastDescriptor, 'id'>) => {
      const toastId = randomId();
      const descriptor: ToastDescriptor = {
        durationMs: 4500,
        variant: 'info',
        ...toast,
        id: toastId
      };

      setToasts((current) => [...current, descriptor]);
      scheduleRemoval(toastId, descriptor.durationMs ?? 0);
      return toastId;
    },
    [scheduleRemoval]
  );

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((timer) => window.clearTimeout(timer));
      timersRef.current = {};
    };
  }, []);

  const contextValue = useMemo<ToastContextValue>(
    () => ({
      pushToast,
      dismissToast
    }),
    [pushToast, dismissToast]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="toast-viewport" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.variant ?? 'info'}`}>
            <div className="toast__content">
              <span className="toast__message">{toast.message}</span>
              {toast.description && <span className="toast__description">{toast.description}</span>}
            </div>
            <button
              type="button"
              className="toast__dismiss"
              aria-label="Dismiss notification"
              onClick={() => dismissToast(toast.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
